import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  SentimentSatisfiedAlt,
  SentimentDissatisfied,
  SentimentNeutral,
} from '@mui/icons-material';

const SimpleSentimentTable = ({ data }) => {
  const [expandedRow, setExpandedRow] = React.useState(null);

  const [sessions, setSessions] = useState([]);

  const handleToggleExpand = (sessionId) => {
    setExpandedRow(expandedRow === sessionId ? null : sessionId);
  };

  const getSentimentIcon = (score) => {
    if (score === null || score === undefined) return <SentimentNeutral color="disabled" />;
    if (score > 0.3) return <SentimentSatisfiedAlt color="success" />;
    if (score < -0.3) return <SentimentDissatisfied color="error" />;
    return <SentimentNeutral color="warning" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const parseSentimentData = (data) => {
    const sessions = Object.entries(data);
    console.log(sessions)
    const sessionsWithAverages = sessions.map(([sessionId, sessionData]) => {
      const avgSentiment = sessionData.sentimentSum / sessionData.responses.filter(r => r.sentiment_score !== null).length;
      const positivePercentage = (sessionData.positiveCount / sessionData.totalCount) * 100;
      
      return {
        sessionId,
        ...sessionData,
        avgSentiment: isNaN(avgSentiment) ? null : avgSentiment,
        positivePercentage
      };
    });
    console.log(sessionsWithAverages)
    setSessions(sessionsWithAverages);
  }


  useEffect (() => {
    if (data) {
      parseSentimentData(data);
    }
  }, [data])

  return (
    <Box sx={{ width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
      <Box sx={{ p: 2}}>
        <Stack direction={"row"} spacing={1} sx={{display: 'flex', alignItems: 'center'}}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
            Sentiment analyisis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Surveys sentiment analytics
        </Typography>
          </Box>
        </Stack>
      </Box>
  
      <TableContainer>
        <Table size="small" stickyHeader aria-label="sentiment analysis table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>
                <Typography variant="subtitle2" fontSize="0.75rem">
                  Session
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontSize="0.75rem">
                  Responses
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontSize="0.75rem">
                  Positive
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontSize="0.75rem">
                  Sentiment
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions && sessions.map((session) => (
              <React.Fragment key={session.sessionId}>
                <TableRow 
                  hover 
                  sx={{ 
                    '& > *': { borderBottom: 'unset' },
                    cursor: 'pointer'
                  }}
                  onClick={() => handleToggleExpand(session.sessionId)}
                >
                  <TableCell padding="checkbox">
                    <IconButton size="small" sx={{ padding: '4px' }}>
                      {expandedRow === session.sessionId ? 
                        <KeyboardArrowUp fontSize="small" /> : 
                        <KeyboardArrowDown fontSize="small" />
                      }
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontSize="0.8rem" fontWeight="medium">
                        Session {session.sessionId}
                      </Typography>
                      <Typography variant="body2" fontSize="0.8rem" fontWeight="medium">
                        Name: {session.first_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {session.program_name || 'General'} • {formatDate(session.session_date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={session.totalCount} 
                      size="small" 
                      variant="outlined"
                      sx={{ height: '24px', fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`${session.positivePercentage.toFixed(0)}% Positive`}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={session.positivePercentage} 
                          color={
                            session.positivePercentage > 70 ? "success" : 
                            session.positivePercentage > 40 ? "warning" : "error"
                          }
                          sx={{ width: '40px', height: '6px' }}
                        />
                        <Typography variant="caption" fontSize="0.7rem">
                          {session.positivePercentage.toFixed(0)}%
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {session.avgSentiment !== null ? (
                      <Tooltip title={`Score: ${session.avgSentiment.toFixed(3)}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getSentimentIcon(session.avgSentiment)}
                        </Box>
                      </Tooltip>
                    ) : (
                      <SentimentNeutral color="disabled" />
                    )}
                  </TableCell>
                </TableRow>
                
                {/* Expanded Row with Detailed Responses */}
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={expandedRow === session.sessionId} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="subtitle2" gutterBottom fontSize="0.8rem">
                          Session {session.sessionId} Details
                        </Typography>
                        <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                          {session.responses.map((response, index) => (
                            <Paper 
                              key={index} 
                              sx={{ 
                                p: 1, 
                                mb: 0.5, 
                                bgcolor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ mt: 0.2 }}>
                                  {response.sentiment_score !== null ? (
                                    getSentimentIcon(response.sentiment_score)
                                  ) : (
                                    <SentimentNeutral color="disabled" />
                                  )}
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="caption" fontWeight="medium" display="block">
                                    {response.question_text}
                                  </Typography>
                                  <Typography variant="body2" fontSize="0.75rem">
                                    {response.response_text}
                                  </Typography>
                                  {response.sentiment_score !== null && (
                                    <Typography variant="caption" color="text.secondary">
                                      Score: {response.sentiment_score.toFixed(2)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {sessions.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No session data available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SimpleSentimentTable;